import { useState } from 'react'
import { Alert, Form, Input, InputNumber, Modal, Select, Switch } from 'antd'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { submitOnValid } from '@/shared/lib/submitOnValid'
import { usePermissions } from '@/shared/auth/usePermissions'
import { useActiveBranches } from '@/features/branch/useActiveBranches'
import { branchLabel } from '@/features/branch/branchApi'
import {
  LOGIN_PREFIX,
  NETWORK_STATUSES,
  saveVisp,
  setVispBranches,
  type NetworkCenter,
  type NetworkStatus,
  type Visp,
} from './networkApi'

interface FormValues {
  centerId: string
  name: string
  description?: string
  status: NetworkStatus
  coversAllNas: boolean
  loginPrefix?: string
  quota?: number | null
  quotaReason?: string
  branchIds: string[]
}

export function VispFormModal({
  open,
  visp,
  centers,
  defaultCenterId,
  onClose,
}: {
  open: boolean
  visp: Visp | null
  centers: readonly NetworkCenter[]
  defaultCenterId: string | null
  onClose: () => void
}) {
  const { t } = useTranslation()
  const [form] = Form.useForm<FormValues>()
  const queryClient = useQueryClient()
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { can } = usePermissions()
  const canSetQuota = !visp && can('network.visp.quota')
  const quota = Form.useWatch('quota', form)
  const centerId = Form.useWatch('centerId', form)
  const { options: branchChoices } = useActiveBranches()
  const centerBranches = centers.find((c) => c.id === centerId)?.branchIds ?? []
  const allowedBranches = centerBranches.length > 0
    ? branchChoices.filter((b) => centerBranches.includes(b.id))
    : branchChoices

  const submit = () => {
    submitOnValid(form, async (values) => {
      setSaving(true)
      setError(null)
      try {
        const saved = await saveVisp(visp?.id ?? null, {
          centerId: values.centerId,
          name: values.name.trim(),
          description: values.description?.trim() ?? '',
          status: values.status,
          coversAllNas: values.coversAllNas,
          loginPrefix: values.loginPrefix?.trim() ?? '',
          ...(canSetQuota && values.quota
            ? { quota: values.quota, quotaReason: values.quotaReason?.trim() ?? '' }
            : {}),
        })
        const before = [...(visp?.branchIds ?? [])].sort().join()
        if ([...values.branchIds].sort().join() !== before) {
          await setVispBranches(saved.id, values.branchIds)
        }
        void queryClient.invalidateQueries({ queryKey: queryKeys.network.all })
        void queryClient.invalidateQueries({ queryKey: queryKeys.subscribers.all })
        onClose()
      } catch (err) {
        setError(translateError(err, t))
      } finally {
        setSaving(false)
      }
    })
  }

  return (
    <Modal
      open={open}
      title={visp ? t('network.visps.edit') : t('network.visps.create')}
      onCancel={onClose}
      onOk={submit}
      okText={t('common.save')}
      cancelText={t('common.cancel')}
      confirmLoading={saving}
      destroyOnHidden
      afterOpenChange={(visible) => {
        if (!visible) return
        setError(null)
        form.setFieldsValue({
          centerId: visp?.centerId ?? defaultCenterId ?? undefined,
          name: visp?.name ?? '',
          description: visp?.description ?? '',
          status: visp?.status ?? 'ACTIVE',
          coversAllNas: visp?.coversAllNas ?? false,
          loginPrefix: visp?.loginPrefix ?? '',
          quota: null,
          quotaReason: '',
          branchIds: [...(visp?.branchIds ?? [])],
        })
      }}
    >
      {error ? <Alert type="error" showIcon style={{ marginBottom: 16 }} message={error} /> : null}
      <Form form={form} layout="vertical" disabled={saving}>
        <Form.Item
          name="centerId"
          label={t('network.visps.center')}
          rules={[{ required: true, message: t('network.visps.centerRequired') }]}
        >
          <Select
            showSearch
            optionFilterProp="label"
            options={centers.map((c) => ({ value: c.id, label: c.name }))}
          />
        </Form.Item>
        <Form.Item
          name="name"
          label={t('network.name')}
          rules={[{ required: true, whitespace: true, max: 100, message: t('network.nameRequired') }]}
        >
          <Input autoComplete="off" />
        </Form.Item>
        <Form.Item name="description" label={t('network.description')} rules={[{ max: 500 }]}>
          <Input.TextArea rows={2} maxLength={500} />
        </Form.Item>
        <Form.Item name="status" label={t('network.status.label')}>
          <Select
            disabled={visp?.isDefault}
            options={NETWORK_STATUSES.map((s) => ({ value: s, label: t(`network.status.${s}`) }))}
          />
        </Form.Item>
        <Form.Item
          name="coversAllNas"
          label={t('network.visps.coversAllNas')}
          extra={t('network.visps.coversAllNasHint')}
          valuePropName="checked"
        >
          <Switch disabled={visp?.isDefault} />
        </Form.Item>
        <Form.Item
          name="loginPrefix"
          label={t('network.visps.loginPrefix')}
          extra={t('network.visps.loginPrefixHint')}
          rules={[
            { required: Boolean(quota), message: t('network.visps.loginPrefixInvalid') },
            { pattern: LOGIN_PREFIX, message: t('network.visps.loginPrefixInvalid') },
          ]}
        >
          <Input autoComplete="off" maxLength={24} disabled={visp?.quota != null} />
        </Form.Item>
        {branchChoices.length > 0 ? (
          <Form.Item name="branchIds" label={t('branch.labelPlural')} extra={t('branch.vispHint')}>
            <Select<string[]>
              mode="multiple"
              allowClear
              optionFilterProp="label"
              placeholder={t('branch.none')}
              options={allowedBranches.map((b) => ({ value: b.id, label: branchLabel(b) }))}
            />
          </Form.Item>
        ) : null}
        {canSetQuota ? (
          <>
            <Form.Item name="quota" label={t('network.visps.quota')} extra={t('network.visps.quotaHint')}>
              <InputNumber min={1} max={10000} precision={0} style={{ width: '100%' }} />
            </Form.Item>
            {quota ? (
              <Form.Item
                name="quotaReason"
                label={t('network.visps.quotaReason')}
                rules={[{ required: true, whitespace: true, max: 500, message: t('network.visps.quotaReasonRequired') }]}
              >
                <Input.TextArea rows={2} maxLength={500} />
              </Form.Item>
            ) : null}
          </>
        ) : null}
      </Form>
    </Modal>
  )
}
